import type { Api } from "../../services/api";
import type { CurrentUser } from "../user/hooks/useCurrentUser";
import type { SupportedLanguage } from "../../interfaces";
import type { ContactLineKind } from "../../tools/analytics/domains/partners.analytics";
import { fillDisclaimerVariables } from "../../../../api/src/shared/domain/room-disclaimer";

type PrivateRoom = Api["get"]["/webapp/private/rooms/:id"]["output"]["body"];

/**
 * The client disclaimer of the room in `language`, with its variables filled
 * in, or null when the room has none: the sheet then shows the default text.
 * A client text is one the admin turned on. A room that never did still holds
 * the old admin placeholder in `content` (measured on production on
 * 2026-10-08), which is not a text the client validated. A language with no
 * client text shows the default text of that language, as the admin tab
 * previews it.
 */
export function clientDisclaimer(
  room: Pick<PrivateRoom, "privacyMarketingSettings">,
  language: SupportedLanguage,
  names: { roomName: string; partnerName: string }
): string | null {
  const settings = room.privacyMarketingSettings;
  if (settings === null || settings.disclaimer === null) return null;
  if (settings.disclaimer.enable !== true) return null;
  const content = settings.disclaimer.content;
  if (content === null) return null;
  const text = content[language];
  if (text === null || text.trim() === "") return null;
  return fillDisclaimerVariables(text, names).trim();
}

/**
 * Splits a disclaimer at its last whole sentence within `limit`. A text within
 * the limit, or one with no sentence end inside it, is not folded.
 */
export function foldDisclaimer(
  text: string,
  limit: number
): { shown: string; folded: string | null } {
  if (text.length <= limit) return { shown: text, folded: null };
  const window = text.slice(0, limit + 1);
  const ends = [...window.matchAll(/[.!?](?=\s)/g)];
  const last = ends.at(-1);
  if (last === undefined) return { shown: text, folded: null };
  const cut = last.index + 1;
  return { shown: text.slice(0, cut), folded: text.slice(cut).trim() };
}

/**
 * What the entry into a room does with a welcome sheet the member never saw
 * (docs/partner-objection.md, REQ-14). The server makes a user a member before
 * the app runs any join: an invited signup that stops before the end of its
 * onboarding, or a member that an admin adds. The entry shows them the sheet
 * that their join never opened.
 *
 * `wait`: not decidable yet, check again. `skip`: nothing to show at this
 * entry. A non-member skips, because a join on the room page opens its own
 * welcome. `welcome`: open the sheet.
 */
export function roomEntryWelcome(entry: {
  roomId: string;
  isOnSharedContent: boolean;
  user: {
    rooms: CurrentUser["rooms"];
    roomUserSettings: Pick<
      CurrentUser["roomUserSettings"][number],
      "roomId" | "disclaimer"
    >[];
  } | null;
}): "wait" | "skip" | "welcome" {
  // A shared article is not the room: the sheet waits for the room itself.
  if (entry.isOnSharedContent) return "wait";
  if (entry.user === null) return "wait";
  if (!entry.user.rooms.includes(entry.roomId)) return "skip";
  const setting = entry.user.roomUserSettings.find(
    (s) => s.roomId === entry.roomId
  );
  const hasSeenWelcome = setting !== undefined && setting.disclaimer === true;
  return hasSeenWelcome ? "skip" : "welcome";
}

/**
 * What the contact line says (REQ-07): it confirms an objection, informs a
 * member, and is conditional before a join. A room with no partner has none.
 */
export function contactLineKind(
  partnerContact: PrivateRoom["partnerContact"],
  isMember: boolean
): ContactLineKind {
  switch (partnerContact) {
    case "none":
      return "none";
    // Not informed, or the information period runs: no line is true yet
    // (docs/partner-objection.md, REQ-07).
    case "unanswered":
      return "none";
    case "objects":
      return "confirms";
    case "shares":
      return isMember ? "informs" : "conditional";
    default:
      throw new Error(partnerContact satisfies never);
  }
}
